import * as React from 'react';
import styled from '../../../../styled-components';

const DEFAULT_COLOR = '#2d3035';
const DEFAULT_HOVER_COLOR = '#00c2ff';

export interface IconProps {
  color?: string;
  hover?: boolean;
  hoverColor?: string;
  size?: number;
  unit?: 'px' | 'em';
  rotateDegrees?: number; // degrees
  ariaLabel?: string;
  className?: string;
  title?: string;
  feColorMatrix?: string;
}

const IconHover = (hoverColor: string = DEFAULT_HOVER_COLOR) => `
  :hover {
    cursor: pointer;

    .main-path {
      fill: ${hoverColor};
    }
  }
`;

const extractUnnecessaryProps = (rest: Partial<IconProps>) => {
  const extractedRest = { ...rest };
  delete extractedRest.hoverColor;
  delete extractedRest.rotateDegrees;
  return extractedRest;
};

const SVGWrapper = styled(
  (props: { hover?: boolean; hoverColor?: string; children: React.ReactElement; className?: string } & IconProps) => {
    return (
      <span className={props.className} data-testid="react-icon">
        {props.children}
      </span>
    );
  }
)`
  display: inherit; // Without this the wrapper often got additional pixels
  svg {
    ${(props) => props.hover && IconHover(props.hoverColor)};
    transform: ${(props) => (props.rotateDegrees ? `rotate(${props.rotateDegrees}deg)` : null)};
  }
`;

export const Clock: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M12,1 C18.075,1 23,5.925 23,12 C23,18.075 18.075,23 12,23 C5.925,23 1,18.075 1,12 C1,5.925 5.925,1 12,1 Z M12,3 C7.037,3 3,7.037 3,12 C3,16.963 7.037,21 12,21 C16.963,21 21,16.963 21,12 C21,7.037 16.963,3 12,3 Z M13,11.8521 L17.4,14.3931 C17.879,14.6691 18.042,15.2801 17.766,15.7591 C17.49,16.2371 16.879,16.4011 16.4,16.1251 L11,13.0071 L11.013,12.9851 L11,12.9851 L11,5.9851 C11,5.4321 11.448,4.9851 12,4.9851 C12.552,4.9851 13,5.4321 13,5.9851 L13,11.8521 Z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Close: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M18.74 17.281a1 1 0 0 1-1.415 1.414l-5.303-5.303-5.3 5.3a1 1 0 1 1-1.414-1.414l5.3-5.3-5.303-5.303A1 1 0 0 1 6.719 5.26l5.303 5.304 5.307-5.307a1 1 0 0 1 1.414 1.414l-5.307 5.307 5.304 5.303z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const CloseFilled: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0H20V20H0z" />
        <path
          d="M10 1.667c4.604 0 8.333 3.729 8.333 8.333S14.604 18.333 10 18.333 1.667 14.604 1.667 10 5.396 1.667 10 1.667zM7.177 6.008l.079.07L10 8.82l2.744-2.744c.326-.325.853-.325 1.179 0 .3.3.323.774.069 1.1l-.07.079L11.18 10l2.744 2.744c.325.326.325.853 0 1.179-.3.3-.774.323-1.1.069l-.079-.07L10 11.18l-2.744 2.744c-.326.325-.853.325-1.179 0-.3-.3-.323-.774-.069-1.1l.07-.079L8.82 10 6.077 7.256c-.325-.326-.325-.853 0-1.179.276-.275.696-.317 1.016-.127l.084.058z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Healthcare: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" fillRule="nonzero" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M20 6c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h3V4c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v2h3zM9 4v2h6V4H9zm11 16V8H4v12h16zm-4-5h-3v3h-2v-3H8v-2h3v-3h2v3h3v2z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const LoudSpeaker: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M13.514 13.379V6.595l-1.71.774a6.956 6.956 0 0 1-2.889.624H7v4h1.958c1.012 0 1.992.213 2.913.634l1.643.752zm-9.996-2.05c.381.422.922.664 1.482.664V8.009c-1.036.084-1.894.828-1.989 1.775a1.98 1.98 0 0 0 .507 1.545zm10.996-7.286c.527 0 1 .424 1 1v9.893a1.002 1.002 0 0 1-1.416.909l-3.059-1.4a5.014 5.014 0 0 0-2.08-.452H7.005l1.671 6.292a.966.966 0 1 1-1.868.497L5.01 14H5v-.007c-2.335 0-4.22-2.028-3.979-4.41.203-2.012 1.971-3.484 3.98-3.573V6h.11c.025 0 .051-.007.077-.007h3.727c.712 0 1.416-.152 2.064-.446l3.122-1.414a.997.997 0 0 1 .413-.09zm6.18.589a6.97 6.97 0 0 1 2.358 5.23 6.97 6.97 0 0 1-2.072 4.965c-.434.43-1.165.343-1.515-.16a1.005 1.005 0 0 1 .125-1.29 4.958 4.958 0 0 0-.188-7.208c-.372-.335-.463-.88-.194-1.3l.004-.004c.325-.505 1.032-.632 1.483-.233zm-1.756 3.26c.387.56.614 1.237.614 1.971 0 .65-.178 1.257-.49 1.777-.37.62-1.263.642-1.677.051-.212-.304-.267-.718-.067-1.03a1.475 1.475 0 0 0-.033-1.645.996.996 0 0 1-.027-1.11l.002-.003c.39-.608 1.266-.606 1.677-.012z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const ThreeDots: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24">
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M7 12a2 2 0 1 1-3.999.001A2 2 0 0 1 7 12zm7 0a2 2 0 1 1-3.999.001A2 2 0 0 1 14 12zm7 0a2 2 0 1 1-3.999.001A2 2 0 0 1 21 12z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const People: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M3.007 19.5a6.497 6.497 0 0 1 6.09-4.245 6.496 6.496 0 0 1 6.089 4.245H3.007zm6.09-6.245a8.491 8.491 0 0 1 7.977 5.584c.472 1.293-.518 2.661-1.895 2.661H3.014c-1.376 0-2.367-1.368-1.895-2.661a8.493 8.493 0 0 1 7.978-5.584zm13.612 3.75c.82 1.3-.2 2.995-1.736 2.995h-2.545a9.463 9.463 0 0 0-.58-1.948h2.97a4.84 4.84 0 0 0-4.032-2.162c-.073 0-.143.01-.215.013a9.538 9.538 0 0 0-1.9-1.828 6.947 6.947 0 0 1 2.115-.338 6.993 6.993 0 0 1 5.923 3.268zM9.097 11.49a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-5.974a2 2 0 1 0-.001 3.998 2 2 0 0 0 0-3.998zm7.69 6.312a3 3 0 1 1-.001-6 3 3 0 0 1 0 6zm0-4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const PeopleFilled: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      data-testid="people-filled-icon"
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 24 19.613"
      {...extractUnnecessaryProps(rest)}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M 8.8535156 0 C 6.4562715 0 4.4960938 1.9621309 4.4960938 4.359375 C 4.4960938 6.7566191 6.4562715 8.7167969 8.8535156 8.7167969 C 11.25076 8.7167969 13.212891 6.7566191 13.212891 4.359375 C 13.212891 1.9621309 11.25076 0 8.8535156 0 z M 17.244141 2.5058594 C 15.391725 2.5058594 13.974609 3.9229747 13.974609 5.7753906 C 13.974609 7.6278065 15.391725 9.0449219 17.244141 9.0449219 C 19.096557 9.0449219 20.513672 7.6278065 20.513672 5.7753906 C 20.513672 3.9229747 18.987591 2.5058594 17.244141 2.5058594 z M 8.5195312 10.509766 C 7.6157762 10.5373 6.7017009 10.699394 5.8027344 11.005859 C 3.187559 11.98655 1.1174095 13.947734 0.13671875 16.671875 C -0.29914381 17.761531 0.35370298 19.069587 1.4433594 19.396484 C 1.6612907 19.614416 1.9891 19.613281 2.2070312 19.613281 L 15.5 19.613281 C 16.698622 19.613281 17.679688 18.632216 17.679688 17.433594 C 17.679688 17.215662 17.679278 16.998772 17.570312 16.671875 C 16.153759 12.776353 12.435803 10.390451 8.5195312 10.509766 z M 17.4375 11.169922 C 16.611745 11.154599 15.77232 11.278367 14.955078 11.550781 C 17.134391 13.0763 18.552419 15.472306 18.988281 17.978516 L 21.820312 17.978516 C 23.018935 17.978516 24 16.999403 24 15.800781 C 24 15.473884 23.891759 15.037834 23.673828 14.710938 C 22.284516 12.504383 19.914766 11.215892 17.4375 11.169922 z "
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Person: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M12,13.4687 C8.376,13.4687 5.233,15.4997 3.634,18.4847 C2.912,19.8337 3.851,21.4687 5.381,21.4687 L18.619,21.4687 C20.149,21.4687 21.088,19.8337 20.366,18.4847 C18.767,15.4997 15.624,13.4687 12,13.4687 M12,15.4687 C14.764,15.4687 17.294,16.9857 18.603,19.4287 C18.611,19.4447 18.621,19.4637 18.619,19.4687 L5.395,19.4807 C5.379,19.4637 5.389,19.4447 5.397,19.4287 C6.706,16.9857 9.236,15.4687 12,15.4687 Z M12,2.4687 C14.761,2.4687 17,4.7077 17,7.4687 C17,10.2297 14.761,12.4687 12,12.4687 C9.239,12.4687 7,10.2297 7,7.4687 C7,4.7077 9.239,2.4687 12,2.4687 Z M12,10.4687 C13.657,10.4687 15,9.1257 15,7.4687 C15,5.8117 13.657,4.4687 12,4.4687 C10.343,4.4687 9,5.8117 9,7.4687 C9,9.1257 10.343,10.4687 12,10.4687 Z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Brush: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 21 19" {...extractUnnecessaryProps(rest)}>
      <g stroke="none" fill="none" fillRule="evenodd">
        <g transform="translate(-1.000000, -2.500000)">
          <path
            className="main-path"
            d="M6.3021,19.3527 C7.9021,19.3527 9.3021,18.0527 9.4021,16.4527 C9.4021,16.0527 9.2021,15.6527 8.9031,15.3527 C8.6021,15.0527 8.2021,14.8527 7.8021,14.8527 C7.4021,14.8527 7.0021,15.0527 6.7021,15.3527 C6.4021,15.6527 6.2021,16.0527 6.2021,16.4527 C6.2021,17.2517 5.9021,18.0527 5.4021,18.6527 C5.3021,18.7517 5.3021,18.7517 5.2021,18.8527 C5.1021,18.9527 5.0021,18.9527 4.9021,19.0527 C5.3021,19.2517 5.8021,19.3527 6.3021,19.3527 Z M7.8021,12.8527 C8.8021,12.8527 9.7021,13.2517 10.3021,13.8527 C10.9031,14.4527 11.3021,15.3527 11.3021,16.3527 C11.3021,19.1527 9.0021,21.3527 6.3021,21.3527 C5.0021,21.3527 3.7021,20.7517 2.8021,19.8527 C2.4021,19.4527 2.1021,18.9527 1.8021,18.3527 C1.7021,18.1527 1.8021,17.9527 1.9021,17.8527 C2.0981,17.7537 2.1981,17.7517 2.3911,17.7517 L2.4031,17.7517 C2.5021,17.8527 2.6021,17.8527 2.8021,17.8527 C3.6021,17.8527 4.2021,17.2517 4.2021,16.4527 C4.2021,14.4527 5.8021,12.8527 7.8021,12.8527 Z M20.4457,2.648 C20.7147,2.648 20.9667,2.764 21.1507,2.948 C21.4347,3.232 21.5577,3.676 21.3417,4.101 C19.5447,7.639 17.0517,11.156 13.9277,14.401 C13.6727,14.667 13.3357,14.848 12.9747,14.931 C12.9697,14.933 12.9657,14.934 12.9607,14.935 C12.8897,14.951 12.8197,14.959 12.7497,14.959 C12.4877,14.959 12.2417,14.849 12.0567,14.663 C11.9687,14.575 11.8947,14.47 11.8397,14.352 C11.6327,13.904 11.3497,13.485 10.9797,13.115 C10.6237,12.76 10.2207,12.485 9.7927,12.281 C9.6777,12.227 9.5757,12.154 9.4897,12.068 C9.2567,11.836 9.1417,11.505 9.2147,11.169 C9.2307,11.099 9.2467,11.03 9.2637,10.96 C9.3427,10.63 9.5277,10.329 9.7737,10.094 C10.0437,9.836 10.3177,9.589 10.5907,9.339 C10.5897,9.339 10.5897,9.339 10.5887,9.338 C10.6057,9.323 10.6227,9.309 10.6397,9.293 C11.1157,8.86 11.5957,8.438 12.0807,8.031 C12.1317,7.988 12.1817,7.94 12.2337,7.897 C12.2347,7.897 12.2347,7.898 12.2357,7.898 C14.7427,5.817 17.3647,4.094 19.9987,2.757 C20.1477,2.681 20.2987,2.648 20.4457,2.648 Z M13.0277,12.443 C13.2597,12.193 13.4787,11.937 13.7037,11.684 C13.5167,11.445 13.3207,11.214 13.1007,10.995 C12.8827,10.776 12.6517,10.579 12.4137,10.394 C12.1607,10.619 11.9037,10.837 11.6537,11.069 C11.9167,11.26 12.1647,11.472 12.3947,11.702 C12.6247,11.932 12.8367,12.18 13.0277,12.443 Z M15.0057,10.15 C16.0107,8.908 16.9427,7.634 17.7727,6.325 C16.4657,7.155 15.1907,8.086 13.9477,9.092 C14.1367,9.255 14.3367,9.401 14.5157,9.58 C14.6957,9.76 14.8427,9.96 15.0057,10.15 Z"
            fill={color}
          />
        </g>
      </g>
    </svg>
  </SVGWrapper>
);

export const Start: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  title,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      {title && <title>{title}</title>}
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M16.144 11.465c.473.295.47.776 0 1.07l-6.288 3.93c-.473.295-.856.08-.856-.46v-8.01c0-.55.386-.754.856-.46l6.288 3.93zM12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0 2C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const CalendarStar: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g stroke="none" fill="none" fillRule="evenodd">
        <rect fillRule="nonzero" x="0" y="0" width="24" height="24" />
        <path
          className="main-path"
          d="M8,7 C7.4,7 7,6.6 7,6 L7,3 C7,2.4 7.4,2 8,2 C8.6,2 9,2.4 9,3 L9,6 C9,6.6 8.6,7 8,7 Z M19,4 L20,4 C21.1,4 22,4.9 22,6 L22,20 C22,21.1 21.1,22 20,22 L4,22 C2.9,22 2,21.1 2,20 L2,6 C2,4.9 2.9,4 4,4 L5,4 C5.6,4 6,4.4 6,5 C6,5.6 5.6,6 5,6 L4,6 L4,20 L20,20 L20,6 L19,6 C18.4,6 18,5.6 18,5 C18,4.4 18.4,4 19,4 Z M11,6 C10.4,6 10,5.6 10,5 C10,4.4 10.4,4 11,4 L13,4 C13.6,4 14,4.4 14,5 C14,5.6 13.6,6 13,6 L11,6 Z M16,7 C15.4,7 15,6.6 15,6 L15,3 C15,2.4 15.4,2 16,2 C16.6,2 17,2.4 17,3 L17,6 C17,6.6 16.6,7 16,7 Z M16.4,11.3 C16.6,11.3 16.8,11.4 16.9,11.6 C17.1,11.9 17.1,12.3 16.8,12.5 L14.4,14.3 L15.3,17.2 C15.4,17.4 15.3,17.6 15.2,17.8 C15,18 14.7,18.1 14.4,17.9 L12,16.1 L9.6,17.9 C9.4,18 9.2,18.1 9,18 C8.7,17.9 8.6,17.5 8.7,17.2 L9.6,14.3 L7.2,12.5 C7.1,12.4 7,12.2 7,12 C7,11.7 7.3,11.4 7.6,11.3 L10.6,11.3 L11.5,8.4 C11.6,8.2 11.7,8.1 11.9,8 C12.2,7.9 12.5,8.1 12.6,8.4 L13.5,11.3 L16.4,11.3 Z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

/* eslint-disable @typescript-eslint/no-unused-vars */
export const CalendarAdd: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
      <g fill="none" fillRule="evenodd">
        <rect fillRule="nonzero" x="0" y="0" width="24" height="24" />
        <path
          d="M7,6 L7,3 C7,2.4 7.4,2 8,2 C8.6,2 9,2.4 9,3 L9,6 C9,6.6 8.6,7 8,7 C7.4,7 7,6.6 7,6 Z M19,4 L20,4 C21.1,4 22,4.9 22,6 L22,20 C22,21.1 21.1,22 20,22 L4,22 C2.9,22 2,21.1 2,20 L2,6 C2,4.9 2.9,4 4,4 L5,4 C5.6,4 6,4.4 6,5 C6,5.6 5.6,6 5,6 L4,6 L4,20 L20,20 L20,6 L19,6 C18.4,6 18,5.6 18,5 C18,4.4 18.4,4 19,4 Z M13,6 L11,6 C10.4,6 10,5.6 10,5 C10,4.4 10.4,4 11,4 L13,4 C13.6,4 14,4.4 14,5 C14,5.6 13.6,6 13,6 Z M15,6 L15,3 C15,2.4 15.4,2 16,2 C16.6,2 17,2.4 17,3 L17,6 C17,6.6 16.6,7 16,7 C15.4,7 15,6.6 15,6 Z M13,12 L15,12 C15.5522847,12 16,12.4477153 16,13 C16,13.5522847 15.5522847,14 15,14 L13,14 L13,16 C13,16.5522847 12.5522847,17 12,17 C11.4477153,17 11,16.5522847 11,16 L11,14 L9,14 C8.44771525,14 8,13.5522847 8,13 C8,12.4477153 8.44771525,12 9,12 L11,12 L11,10 C11,9.44771525 11.4477153,9 12,9 C12.5522847,9 13,9.44771525 13,10 L13,12 Z"
          className="main-path"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Copy: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M810.667 588.8v-426.667h-426.667v426.667h426.667zM810.667 674.133h-426.667c-46.933 0-85.333-38.4-85.333-85.333v-426.667c0-46.933 38.4-85.333 85.333-85.333h426.667c46.933 0 85.333 38.4 85.333 85.333v426.667c0 46.933-38.4 85.333-85.333 85.333v0zM213.333 332.8v426.667h512c0 46.933-38.4 85.333-85.333 85.333h-426.667c-46.933 0-85.333-38.4-85.333-85.333v-426.667c0-46.933 38.4-85.333 85.333-85.333v85.333z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Edit: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M260.267 388.267c-12.8-12.8-21.333-25.6-21.333-42.667l-68.267-183.467c-8.533-29.867 21.333-59.733 51.2-51.2l174.933 72.533c12.8 4.267 25.6 12.8 38.4 21.333l345.6 345.6-179.2 179.2-341.333-341.333zM874.667 763.733l-59.733 59.733c-34.133 34.133-85.333 34.133-119.467 0l-59.733-59.733 179.2-179.2 59.733 59.733c34.133 29.867 34.133 85.333 0 119.467z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Trash: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M725.333 610.133c0 25.6 17.067 42.667 42.667 42.667s42.667-17.067 42.667-42.667v-469.333c0-46.933-38.4-85.333-85.333-85.333h-426.667c-46.933 0-85.333 38.4-85.333 85.333v469.333c0 25.6 17.067 42.667 42.667 42.667s42.667-17.067 42.667-42.667v-469.333h426.667v469.333zM469.333 268.8c0-25.6-17.067-42.667-42.667-42.667s-42.667 17.067-42.667 42.667v341.333c0 25.6 17.067 42.667 42.667 42.667s42.667-17.067 42.667-42.667v-341.333zM640 268.8c0-25.6-17.067-42.667-42.667-42.667s-42.667 17.067-42.667 42.667v341.333c0 25.6 17.067 42.667 42.667 42.667s42.667-17.067 42.667-42.667v-341.333zM640 780.8h-256l17.067 38.4c12.8 29.867 42.667 46.933 76.8 46.933h64c34.133 0 64-17.067 76.8-46.933l21.333-38.4zM874.667 695.467h-725.333c-25.6 0-42.667 17.067-42.667 42.667s17.067 42.667 42.667 42.667h725.333c25.6 0 42.667-17.067 42.667-42.667s-17.067-42.667-42.667-42.667z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Download: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M478.293 197.973c7.738-9.488 19.351-15.561 32.389-15.786l0.038-0.001c0.199-0.003 0.434-0.005 0.669-0.005 11.723 0 22.342 4.728 30.054 12.381l271.357 271.357c6.377 7.412 10.259 17.129 10.259 27.753 0 23.564-19.103 42.667-42.667 42.667-10.624 0-20.341-3.883-27.809-10.307l-197.917-199.633v497.067c0 23.564-19.103 42.667-42.667 42.667s-42.667-19.103-42.667-42.667v-494.933l-198.827 197.973c-7.412 6.377-17.129 10.259-27.753 10.259-23.564 0-42.667-19.103-42.667-42.667 0-10.624 3.883-20.341 10.307-27.809zM810.667 139.093h-597.333c-23.564 0-42.667-19.103-42.667-42.667s19.103-42.667 42.667-42.667h597.333c23.564 0 42.667 19.103 42.667 42.667s-19.103 42.667-42.667 42.667z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Link: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M631.467 281.6c-17.067 17.067-42.667 17.067-59.733 0l-89.6-89.6c-72.533-72.533-187.733-68.267-251.733 12.8-55.467 68.267-46.933 170.667 17.067 234.667l85.333 85.333c17.067 17.067 17.067 42.667 0 59.733s-42.667 17.067-59.733 0l-81.067-81.067c-98.133-98.133-110.933-260.267-12.8-362.667 98.133-106.667 264.533-106.667 366.933-4.267l89.6 89.6c12.8 12.8 12.8 38.4-4.267 55.467zM836.267 802.133c-102.4 98.133-264.533 85.333-362.667-12.8l-81.067-85.333c-17.067-17.067-17.067-42.667 0-59.733s42.667-17.067 59.733 0l85.333 85.333c64 64 166.4 72.533 234.667 17.067 81.067-64 81.067-183.467 12.8-251.733l-89.6-89.6c-17.067-17.067-17.067-42.667 0-59.733s42.667-17.067 59.733 0l89.6 89.6c102.4 98.133 98.133 264.533-8.533 366.933zM422.4 311.467l243.2 243.2c17.067 17.067 17.067 42.667 0 59.733s-42.667 17.067-59.733 0l-243.2-243.2c-17.067-17.067-17.067-42.667 0-59.733s42.667-17.067 59.733 0z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Check: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M890.88 766.293l-526.080-526.080-229.12 229.12c-7.412 6.377-17.129 10.259-27.753 10.259-23.564 0-42.667-19.103-42.667-42.667 0-10.624 3.883-20.341 10.307-27.809l258.939-258.93c7.712-7.668 18.343-12.407 30.080-12.407s22.368 4.74 30.082 12.409l554.665 554.665c6.377 7.412 10.259 17.129 10.259 27.753 0 23.564-19.103 42.667-42.667 42.667-10.624 0-20.341-3.883-27.809-10.307z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Leave: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M832 864.853h-298.667c-47.128 0-85.333-38.205-85.333-85.333v-100.267c0-23.564 19.103-42.667 42.667-42.667v0c23.564 0 42.667 19.103 42.667 42.667v100.267h298.667v-640h-298.667v113.067c0 23.564-19.103 42.667-42.667 42.667v0c-23.564 0-42.667-19.103-42.667-42.667v-113.067c0-47.128 38.205-85.333 85.333-85.333h298.667c47.128 0 85.333 38.205 85.333 85.333v640c0 47.128-38.205 85.333-85.333 85.333zM725.333 503.467h-472.747l107.947 106.667c7.668 7.712 12.407 18.343 12.407 30.080s-4.74 22.368-12.409 30.082l0.002-0.002c-7.805 8.315-18.866 13.495-31.136 13.495-12.064 0-22.958-5.007-30.718-13.055l-179.639-180.92c-7.668-7.712-12.407-18.343-12.407-30.080s4.74-22.368 12.409-30.082l179.625-182.185c7.712-7.668 18.343-12.407 30.080-12.407s22.368 4.74 30.082 12.409l-0.002-0.002c7.668 7.712 12.407 18.343 12.407 30.080s-4.74 22.368-12.409 30.082l-106.665 110.505h473.173c23.564 0 42.667 19.103 42.667 42.667v0c0 23.564-19.103 42.667-42.667 42.667z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Handraise: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M554.667 525.653c23.564 0 42.667 19.103 42.667 42.667v319.147c0 23.564-19.103 42.667-42.667 42.667s-42.667-19.103-42.667-42.667v-319.147c0-23.564 19.103-42.667 42.667-42.667zM682.667 465.92c23.564 0 42.667 19.103 42.667 42.667v319.147c0 23.564-19.103 42.667-42.667 42.667s-42.667-19.103-42.667-42.667v-318.72c-0.001-0.127-0.002-0.277-0.002-0.427 0-23.564 19.103-42.667 42.667-42.667 0.001 0 0.001 0 0.002 0zM853.333 742.4c0 23.564-19.103 42.667-42.667 42.667s-42.667-19.103-42.667-42.667v-444.587c0.085-2.287 0.133-4.972 0.133-7.669 0-59.063-23.123-112.725-60.811-152.427-37.448-37.516-89.348-60.785-146.682-60.785s-109.234 23.269-146.77 60.879l-145.070 145.070-67.413 66.987c-3.876 3.876-6.274 9.232-6.274 15.147 0 11.83 9.59 21.421 21.421 21.421 5.915 0 11.27-2.398 15.147-6.274l11.52-10.24c9.64-10.054 20.102-19.161 31.33-27.273 19.533-12.023 42.37-18.87 66.806-18.87 70.415 0 127.549 56.858 127.997 127.167v393.856c0 23.564-19.103 42.667-42.667 42.667s-42.667-19.103-42.667-42.667v-394.24c0.010-0.345 0.016-0.75 0.016-1.158 0-23.564-19.103-42.667-42.667-42.667-11.225 0-21.438 4.335-29.055 11.422l-20.454 20.456c-19.229 19.244-45.801 31.147-75.153 31.147-0.129 0-0.258 0-0.387-0.001h0.020c-57.743-1.501-103.98-48.667-103.98-106.633 0-28.265 10.994-53.963 28.939-73.049l213.282-213.278c52.91-53.013 126.060-85.809 206.869-85.809 161.414 0 292.267 130.852 292.267 292.267 0 5.12-0.132 10.209-0.392 15.264l0.029 121.745s0 0 0 2.987z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Info: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M512 930.133c-259.206 0-469.333-210.128-469.333-469.333s210.128-469.333 469.333-469.333c259.206 0 469.333 210.128 469.333 469.333s-210.128 469.333-469.333 469.333zM512 76.8c-212.077 0-384 171.923-384 384s171.923 384 384 384c212.077 0 384-171.923 384-384s-171.923-384-384-384zM555.093 631.467c0-23.564-19.103-42.667-42.667-42.667s-42.667 19.103-42.667 42.667c0 23.564 19.103 42.667 42.667 42.667s42.667-19.103 42.667-42.667zM590.507 340.907c-1.004 0.167-2.16 0.263-3.338 0.263-9.75 0-17.972-6.54-20.518-15.472-0.753-1.943-1.613-3.489-2.64-4.907l0.044 0.063-4.267-4.267-3.84-2.133c-2.779-1.503-6.083-2.386-9.593-2.386-1.132 0-2.243 0.092-3.325 0.269-9.882 2.276-17.23 11.098-17.23 21.637 0 1.243 0.102 2.462 0.299 3.65l29.422 170.538c0 23.564-19.103 42.667-42.667 42.667h-86.187c-23.564 0-42.667-19.103-42.667-42.667s19.103-42.667 42.667-42.667h35.413l-20.48-115.2v-8.533c-0.189-1.664-0.296-3.593-0.296-5.547s0.108-3.883 0.317-5.781c-0.169-1.046-0.254-2.53-0.254-4.033s0.084-2.987 0.249-4.446c11.439-29.647 37.933-51.307 69.931-55.664 7.014-2.053 14.555-3.206 22.365-3.206 34.094 0 63.056 21.972 73.499 52.529 0.377 1.683 0.5 2.989 0.5 4.325 0 10.393-7.431 19.050-17.27 20.947z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const AlertSVG: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M968.533 119.467l-419.413 725.333c-7.524 12.801-21.228 21.258-36.907 21.258s-29.382-8.457-36.798-21.058l-419.949-725.533c-3.594-6.12-5.716-13.479-5.716-21.334 0-23.564 19.103-42.667 42.667-42.667 0.060 0 0.119 0 0.179 0h839.244c23.454 0.146 42.41 19.192 42.41 42.666 0 7.855-2.123 15.214-5.826 21.535zM166.4 139.947l345.6 597.333 345.6-597.333h-691.2zM554.667 226.133c0-23.564-19.103-42.667-42.667-42.667s-42.667 19.103-42.667 42.667c0 23.564 19.103 42.667 42.667 42.667s42.667-19.103 42.667-42.667zM469.333 524.8v-170.667c0-23.564 19.103-42.667 42.667-42.667s42.667 19.103 42.667 42.667v170.667c0 23.564-19.103 42.667-42.667 42.667s-42.667-19.103-42.667-42.667z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const DownArrow: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 1000 1000"
      {...extractUnnecessaryProps(rest)}
      style={{ transform: 'scaleY(-1)' }}
    >
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M276.053 523.52l206.080-206.080c7.712-7.668 18.343-12.407 30.080-12.407s22.368 4.74 30.082 12.409l206.078 206.078c6.643 6.511 10.761 15.576 10.761 25.604 0 19.794-16.046 35.84-35.84 35.84-0.183 0-0.366-0.001-0.549-0.004h-421.519c-0.155 0.002-0.338 0.004-0.521 0.004-19.794 0-35.84-16.046-35.84-35.84 0-10.027 4.118-19.093 10.755-25.598z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const LockClosed: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      data-testid="lock-closed-icon"
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 24 24"
      {...extractUnnecessaryProps(rest)}
    >
      <g fill="none" fillRule="evenodd">
        <path className="border-path" d="M0 0h24v24H0z" />
        <path
          className="main-path"
          d="M6 20.995h12v-12H6v12zM9 6v.995h6V6c0-1.654-1.346-3-3-3S9 4.346 9 6zm9 .995a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2h1v-1h.001a4.999 4.999 0 0 1 9.999 0v1h1zM13.5 14.5c0 .443-.195.836-.5 1.111v1.399a1 1 0 0 1-2 0v-1.399a1.49 1.49 0 0 1-.5-1.111 1.5 1.5 0 0 1 3 0z"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Chat: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 20.291" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M 12 0 C 5.3454545 0 0 4.3638137 0 9.8183594 C 0 15.272904 5.3454545 19.636719 12 19.636719 C 13.745455 19.636719 15.490305 19.309127 17.017578 18.763672 L 21.164062 20.181641 C 21.273153 20.181641 21.381143 20.291016 21.490234 20.291016 C 22.14478 20.291016 22.691122 19.636968 22.582031 18.982422 L 21.818359 15.380859 C 23.12745 13.744495 24 11.891086 24 9.8183594 C 24 4.3638137 18.654545 2.3684758e-15 12 0 z M 12 2.1816406 C 17.454545 2.1816406 21.818359 5.5638137 21.818359 9.8183594 C 21.818359 11.345631 21.27255 12.762891 20.181641 13.962891 L 19.527344 14.726562 L 19.746094 15.818359 L 20.072266 17.455078 L 17.671875 16.691406 L 16.908203 16.472656 L 16.144531 16.691406 C 14.83544 17.236861 13.418182 17.455078 12 17.455078 C 6.5454545 17.455078 2.1816406 14.072904 2.1816406 9.8183594 C 2.1816406 5.5638137 6.5454545 2.1816406 12 2.1816406 z "
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const ContentChat: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 20.291" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M12 2.5C5.925 2.5 1 6.753 1 12s4.925 9.5 11 9.5a12.37 12.37 0 004.656-.901c.122-.049.25-.075.378-.075.083 0 .166.011.247.033l3.198.857a1.002 1.002 0 001.225-1.225l-.6-2.239c-.08-.298-.009-.61.174-.859C22.365 15.619 23 13.875 23 12c0-5.247-4.925-9.5-11-9.5"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Calendar: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 22.736 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="M 7.578125 0 C 6.8202303 0 6.3164063 0.50577713 6.3164062 1.2636719 L 6.3164062 5.0527344 C 6.3164062 5.8106291 6.8202303 6.3164063 7.578125 6.3164062 C 8.3360197 6.3164062 8.8417969 5.8106291 8.8417969 5.0527344 L 8.8417969 1.2636719 C 8.8417969 0.50577713 8.3360197 0 7.578125 0 z M 15.158203 0 C 14.400308 0 13.894531 0.50577713 13.894531 1.2636719 L 13.894531 5.0527344 C 13.894531 5.8106291 14.400308 6.3164063 15.158203 6.3164062 C 15.916098 6.3164062 16.421875 5.8106291 16.421875 5.0527344 L 16.421875 1.2636719 C 16.421875 0.50577713 15.916098 0 15.158203 0 z M 2.5253906 2.5253906 C 1.1359169 2.5253906 2.9605947e-16 3.6632607 0 5.0527344 L 0 21.474609 C 0 22.864083 1.1359169 24 2.5253906 24 L 20.210938 24 C 21.600411 24 22.736328 22.864083 22.736328 21.474609 L 22.736328 5.0527344 C 22.736328 3.6632607 21.600411 2.5253906 20.210938 2.5253906 L 17.683594 2.5253906 L 17.683594 5.0527344 L 20.210938 5.0527344 L 20.210938 7.578125 L 2.5253906 7.578125 L 2.5253906 5.0527344 L 5.0527344 5.0527344 L 5.0527344 2.5253906 L 2.5253906 2.5253906 z M 10.105469 2.5253906 L 10.105469 5.0527344 L 12.630859 5.0527344 L 12.630859 2.5253906 L 10.105469 2.5253906 z M 2.5253906 10.105469 L 20.210938 10.105469 L 20.210938 21.474609 L 2.5253906 21.474609 L 2.5253906 10.105469 z M 11.369141 12.630859 A 1.2631579 1.2631579 0 0 0 10.105469 13.894531 A 1.2631579 1.2631579 0 0 0 11.369141 15.158203 A 1.2631579 1.2631579 0 0 0 12.630859 13.894531 A 1.2631579 1.2631579 0 0 0 11.369141 12.630859 z M 16.421875 12.630859 A 1.2631579 1.2631579 0 0 0 15.158203 13.894531 A 1.2631579 1.2631579 0 0 0 16.421875 15.158203 A 1.2631579 1.2631579 0 0 0 17.683594 13.894531 A 1.2631579 1.2631579 0 0 0 16.421875 12.630859 z M 6.3164062 16.421875 A 1.2631579 1.2631579 0 0 0 5.0527344 17.683594 A 1.2631579 1.2631579 0 0 0 6.3164062 18.947266 A 1.2631579 1.2631579 0 0 0 7.578125 17.683594 A 1.2631579 1.2631579 0 0 0 6.3164062 16.421875 z M 11.369141 16.421875 A 1.2631579 1.2631579 0 0 0 10.105469 17.683594 A 1.2631579 1.2631579 0 0 0 11.369141 18.947266 A 1.2631579 1.2631579 0 0 0 12.630859 17.683594 A 1.2631579 1.2631579 0 0 0 11.369141 16.421875 z M 16.421875 16.421875 A 1.2631579 1.2631579 0 0 0 15.158203 17.683594 A 1.2631579 1.2631579 0 0 0 16.421875 18.947266 A 1.2631579 1.2631579 0 0 0 17.683594 17.683594 A 1.2631579 1.2631579 0 0 0 16.421875 16.421875 z "
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Settings: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <path
          className="main-path"
          d="m 16.36364,12 c 0,2.4 -1.96364,4.254545 -4.254545,4.254545 C 9.81818,16.254545 7.7454545,14.4 7.7454545,12 7.7454545,9.6 9.6,7.7454545 12,7.7454545 14.4,7.7454545 16.36364,9.6 16.36364,12 Z m -2.18182,0 c 0,-1.2 -0.98182,-2.0727271 -2.072725,-2.0727271 -1.090915,0 -2.072735,0.9818221 -2.072735,2.0727271 0,1.090905 0.98182,2.072725 2.072735,2.072725 C 13.2,14.072725 14.18182,13.2 14.18182,12 Z M 21.272725,8.836364 C 22.8,9.0545455 24,10.254544 24,11.78182 c 0,0.109084 0,0.109084 0,0.109084 C 24,12 24,12 24,12 c 0,1.527275 -1.2,2.83636 -2.727275,2.945455 l -1.309085,0.32727 0.87272,1.090915 c 0.98182,1.2 0.872735,2.945455 -0.21818,4.03636 0,0 -0.109085,0.109095 -0.109085,0.109095 0,0 0,0 0,0 0,0 0,0 -0.109095,0.109085 -1.090905,1.090915 -2.83636,1.090915 -4.03636,0.21818 L 15.272725,19.96364 15.16364,21.272725 C 14.945455,22.8 13.745455,24 12.21818,24 12.109095,24 12.109095,24 12.109095,24 12,24 12,24 11.890905,24 10.36364,24 9.054546,22.8 8.9454538,21.272725 L 8.7272701,19.96364 7.636364,20.727275 C 6.436364,21.709095 4.6909095,21.6 3.6,20.509095 3.4909095,20.4 3.4909095,20.4 3.4909095,20.4 c 0,0 0,0 -0.1090915,-0.109095 -1.0909085,-1.2 -1.2,-2.83636 -0.218182,-4.03636 L 4.036364,15.16364 2.7272725,15.054545 C 1.2,14.945455 0,13.63636 0,12.21818 0,12.109095 0,12.109095 0,12 c 0,0 0,0 0,-0.109096 0,0 0,0 0,-0.109084 C 0,10.254544 1.2,8.9454545 2.7272725,8.836364 L 4.036364,8.7272725 3.2727275,7.636364 C 2.2909095,6.436364 2.2909095,4.6909095 3.381818,3.6 c 0,0 0.1090915,-0.1090904 0.1090915,-0.1090904 0,0 0,0 0.1090905,-0.1090916 C 4.6909095,2.2909096 6.436364,2.2909096 7.636364,3.163636 L 8.7272701,4.036364 8.836369,2.7272723 C 9.054546,1.2 10.36364,0 11.78182,0 11.890905,0 11.890905,0 12,0 c 0.109095,0 0.109095,0 0.109095,0 1.527265,0 2.83636,1.2 2.94545,2.7272723 L 15.272725,4.036364 16.36364,3.2727277 C 17.56364,2.2909096 19.309095,2.4 20.4,3.4909096 c 0,0 0,0 0.109095,0.1090904 0.109085,0.1090905 0.109085,0.1090905 0.109085,0.1090905 1.090915,1.0909095 1.090915,2.836364 0.21818,4.036364 L 19.96364,8.836364 Z m 0.545455,3.05454 c 0,-0.436361 -0.327275,-0.763628 -0.763635,-0.872724 l -2.72727,-0.327276 -0.21818,-0.654541 C 18,9.7090905 17.890905,9.381818 17.672725,9.0545455 L 17.345455,8.4 17.78182,7.8545455 19.090905,6.218182 c 0.327275,-0.3272725 0.21819,-0.8727275 0,-1.2 0,0 0,0 -0.109085,-0.1090915 0,0 0,0 -0.109095,-0.1090905 -0.32727,-0.3272725 -0.76363,-0.3272725 -1.2,0 L 15.490905,6.5454545 14.83636,6.218182 C 14.509095,6 14.18182,5.8909095 13.854545,5.781818 L 13.2,5.563636 13.090905,4.9090905 12.872725,2.836364 C 12.872725,2.4 12.43636,2.0727276 12,2.0727276 c 0,0 0,0 -0.109095,0 -0.109085,0 -0.109085,0 -0.109085,0 -0.436365,0 -0.76364,0.3272724 -0.872725,0.7636364 L 10.58182,5.563636 9.927275,5.781818 C 9.709094,6.1090905 9.381819,6.218182 9.163632,6.3272725 L 8.5090883,6.6545455 7.963636,6.218182 6.3272725,4.9090905 c -0.436363,-0.218181 -0.872727,-0.218181 -1.2,0.1090915 0,0 0,0 -0.1090905,0.1090905 0,0 -0.1090915,0.1090915 -0.1090915,0.1090915 -0.218181,0.2181815 -0.218181,0.6545455 0,1.0909085 l 1.745455,2.181818 -0.327273,0.6545455 C 6.218182,9.4909095 6,9.818182 5.8909095,10.036363 L 5.6727275,10.690904 5.018182,10.8 2.9454545,11.01818 c -0.436364,0 -0.7636365,0.436363 -0.7636365,0.872724 0,0 0,0 0,0.109096 0,0 0,0 0,0.109095 0,0.43636 0.3272725,0.76363 0.7636365,0.872725 L 5.781818,13.309095 6,13.96364 c 0.1090905,0.327265 0.218182,0.65454 0.436364,0.87272 L 6.763636,15.490905 6.3272725,16.03636 5.018182,17.672725 c -0.3272725,0.327275 -0.218182,0.87273 0,1.2 0,0 0,0 0,0 0.1090905,0.109095 0.1090905,0.109095 0.1090905,0.109095 0,0 0,0 0,0 0.327273,0.327275 0.763637,0.327275 1.2,0 L 8.5090883,17.23636 9.163632,17.56364 C 9.490909,17.672725 9.81818,17.890905 10.03636,18 l 0.654545,0.21818 0.109095,0.654545 0.21818,2.07273 c 0,0.436365 0.436365,0.76364 0.872725,0.76364 0,0 0,0 0.109095,0 0.109095,0 0.109095,0 0.109095,0 0.43636,0 0.76363,-0.327275 0.872725,-0.76364 L 13.309095,18.21818 13.96364,18 c 0.327265,-0.109095 0.65454,-0.21818 0.981815,-0.43636 L 15.6,17.23636 l 0.545455,0.436365 1.636365,1.309095 c 0.327275,0.327275 0.872725,0.21818 1.2,0 0.109085,-0.109095 0.109085,-0.109095 0.109085,-0.109095 0,0 0.109095,-0.109085 0.109095,-0.109085 0.327275,-0.32728 0.327275,-0.76364 0,-1.2 l -1.745455,-2.18182 0.327275,-0.654545 c 0.109085,-0.327275 0.327275,-0.65455 0.43636,-0.98182 l 0.21818,-0.65455 0.654545,-0.109085 2.072735,-0.21818 c 0.43636,0 0.763635,-0.436365 0.763635,-0.872736 0,0 0,0 0,-0.109084 -0.109095,0.109084 -0.109095,0.109084 -0.109095,0.109084"
          fill={color}
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Gift: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <path
        fill={color}
        fillRule="evenodd"
        d="M14.0502525,3.02512627 C15.4170876,1.65829124 17.633165,1.65829124 19,3.02512627 C20.0750512,4.10017751 20.3045468,5.70058973 19.6884866,6.99994283 L20,7 C21.1045695,7 22,7.8954305 22,9 L22,11 C22,12.1045695 21.1045695,13 20,13 L20,20 C20,21.1045695 19.1045695,22 18,22 L6,22 C4.8954305,22 4,21.1045695 4,20 L4,13 C2.8954305,13 2,12.1045695 2,11 L2,9 C2,7.8954305 2.8954305,7 4,7 L4.33663971,6.99994283 C3.7205795,5.70058973 3.95007502,4.10017751 5.02512627,3.02512627 C6.39196129,1.65829124 8.60803871,1.65829124 9.97487373,3.02512627 L10.349276,3.40615634 C10.9474741,4.02745437 11.5967838,4.77105822 12.0136794,5.51455286 C12.4283425,4.77105822 13.0776521,4.02745437 13.6758503,3.40615634 L13.6758503,3.40615634 Z M11,12.999 L6,13 L6,20 L11,19.999 L11,12.999 Z M18,13 L13,12.999 L13,19.999 L18,20 L18,13 Z M8.32606377,9.00050721 L4,9 L4,11 L11,10.999 L11,8.999 L8.75979352,9.00043643 C8.615421,9.00404705 8.4705797,9.00407178 8.32606377,9.00050721 Z M20,9 L15.6992837,9.00050175 C15.5546943,9.00407181 15.4097789,9.00404889 15.2653327,9.00043643 L13,8.999 L13,10.999 L20,11 L20,9 Z M17.5857864,4.43933983 C17,3.85355339 16.0502525,3.85355339 15.4644661,4.43933983 C14.633047,5.27075894 14.0717208,5.93007849 13.7566213,6.49147117 L13.7566213,6.49147117 L13.7055278,6.58603885 L13.640927,6.71887352 L13.6985493,6.74741125 C14.6184487,7.14173939 17.0657691,7.08067755 17.5857864,6.56066017 C18.1715729,5.97487373 18.1715729,5.02512627 17.5857864,4.43933983 Z M6.43933983,4.43933983 C5.85355339,5.02512627 5.85355339,5.97487373 6.43933983,6.56066017 C6.9593572,7.08067755 9.40667755,7.14173939 10.326577,6.74741125 L10.3841993,6.71887352 L10.3195985,6.58603885 L10.2685049,6.49147117 C9.95340545,5.93007849 9.39207928,5.27075894 8.56066017,4.43933983 C7.97487373,3.85355339 7.02512627,3.85355339 6.43933983,4.43933983 Z"
      />
    </svg>
  </SVGWrapper>
);

export const Meetings: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <path
        fill="#FFF"
        fillRule="evenodd"
        d="M10,4 L14,4 L14,6 C14,7.1045695 14.8954305,8 16,8 C17.1045695,8 18,7.1045695 18,6 L18,4 L20,4 C21.1045695,4 22,4.8954305 22,6 L22,20 C22,21.1045695 21.1045695,22 20,22 L4,22 C2.8954305,22 2,21.1045695 2,20 L2,6 C2,4.8954305 2.8954305,4 4,4 L6,4 L6,6 C6,7.1045695 6.8954305,8 8,8 C9.1045695,8 10,7.1045695 10,6 L10,4 Z M13.0666667,17 C13.5823333,17 14,16.5525 14,16 L14,12 C14,11.4475 13.5823333,11 13.0666667,11 L7.93333333,11 C7.41766667,11 7,11.4475 7,12 L7,16 C7,16.5525 7.41766667,17 7.93333333,17 L13.0666667,17 Z M18,16.5291901 L18,11.4708099 C18,11.2135432 17.8018142,11.0990929 17.5573397,11.2213302 L15,12.5 L15,15.5 L17.5573397,16.7786698 C17.8031616,16.9015808 18,16.7892112 18,16.5291901 Z M8,2 C8.55228475,2 9,2.44771525 9,3 L9,6 C9,6.55228475 8.55228475,7 8,7 C7.44771525,7 7,6.55228475 7,6 L7,3 C7,2.44771525 7.44771525,2 8,2 Z M16,2 C16.5522847,2 17,2.44771525 17,3 L17,6 C17,6.55228475 16.5522847,7 16,7 C15.4477153,7 15,6.55228475 15,6 L15,3 C15,2.44771525 15.4477153,2 16,2 Z"
      />
    </svg>
  </SVGWrapper>
);

export const StarEmpty: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg width={`${size}${unit}`} height={`${size}${unit}`} viewBox="0 0 24 24" {...extractUnnecessaryProps(rest)}>
      <g fill="none" fillRule="evenodd">
        <rect width="24" height="24" />
        <path
          fill={color}
          d="M16.4202,11.7241 L14.7782,12.9171 L15.3902,14.8021 L16.4862,18.1741 L13.6172,16.0901 L12.0182,14.9221 L10.4112,16.0891 L7.5432,18.1731 L8.6382,14.8011 L9.2482,12.8991 L7.6382,11.7241 L4.7702,9.6401 L8.3162,9.6401 L10.2942,9.6401 L10.9042,7.7381 L11.9992,4.3661 L13.0952,7.7381 L13.7132,9.6401 L15.7432,9.6401 L19.2882,9.6401 L16.4202,11.7241 Z M21.2562,7.7381 L15.0952,7.7381 L13.1912,1.8781 C12.8162,0.7241 11.1832,0.7241 10.8082,1.8781 L8.9042,7.7381 L2.7432,7.7381 C1.5292,7.7381 1.0242,9.2911 2.0062,10.0041 L6.9912,13.6261 L5.0872,19.4861 C4.7122,20.6401 6.0332,21.6001 7.0152,20.8861 L11.9992,17.2651 L16.9842,20.8861 C17.9662,21.6001 19.2872,20.6401 18.9122,19.4861 L17.0082,13.6261 L21.9932,10.0041 C22.9742,9.2911 22.4702,7.7381 21.2562,7.7381 L21.2562,7.7381 Z"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const StarFull: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 24 24"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <g fill="none" fillRule="evenodd">
        <rect width="24" height="24" />
        <path
          fill={color}
          d="M21.2568,7.7567 L15.0958,7.7567 L13.1918,1.8967 C12.8168,0.7427 11.1838,0.7427 10.8088,1.8967 L8.9048,7.7567 L2.7438,7.7567 C1.5298,7.7567 1.0248,9.3097 2.0068,10.0237 L6.9918,13.6447 L5.0878,19.5047 C4.7128,20.6587 6.0338,21.6187 7.0158,20.9047 L11.9998,17.2837 L16.9848,20.9047 C17.9668,21.6187 19.2878,20.6587 18.9128,19.5047 L17.0088,13.6447 L21.9928,10.0227 C22.9748,9.3097 22.4708,7.7567 21.2568,7.7567"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Search: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 20 20"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <g fill="none" fillRule="evenodd">
        <path fillRule="nonzero" d="M0 0H20V20H0z" />
        <path
          fill="#9AA8BB"
          d="M3.058 9.986C2.182 6.034 5.625 2.59 9.576 3.467c1.992.441 3.599 2.048 4.04 4.04.876 3.951-2.566 7.394-6.519 6.519-1.99-.442-3.597-2.049-4.04-4.04m14.27 6.163l-3.256-3.256c1.07-1.477 1.594-3.377 1.24-5.408-.51-2.917-2.876-5.258-5.799-5.728C4.676.981.56 5.104 1.352 9.941c.49 2.997 2.944 5.386 5.95 5.815 2.16.307 4.163-.367 5.64-1.635l3.207 3.206c.326.326.853.326 1.178 0 .326-.325.326-.853 0-1.178"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const RightArrow: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 20 20"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <g fill="none" fillRule="evenodd">
        <path fillRule="nonzero" d="M0 0H20V20H0z" />
        <path
          fill="#9AA8BB"
          d="M1.667 9.137h14.306l-5.646-5.645c-.325-.325-.325-.853 0-1.179.326-.325.854-.325 1.18 0l7.07 7.071c.325.326.325.854 0 1.18l-7.07 7.07c-.326.325-.854.325-1.18 0-.325-.326-.325-.853 0-1.178l5.653-5.653H1.667c-.46 0-.834-.373-.834-.833 0-.46.374-.833.834-.833z"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Camera: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 22 22"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <g fill="none" fillRule="evenodd">
        <path fillRule="nonzero" d="M0 0H24V24H0z" />
        <path
          fill={color}
          fillOpacity=".67"
          d="M20 3c1.104 0 2 .896 2 2v14c0 1.104-.896 2-2 2H4c-1.104 0-2-.896-2-2V5c0-1.104.896-2 2-2h16zm-5 7V9c0-.552-.448-1-1-1H7c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h7c.552 0 1-.448 1-1v-1l3.276 1.638c.247.124.548.024.671-.223.035-.07.053-.146.053-.224V8.809c0-.276-.224-.5-.5-.5-.078 0-.154.018-.224.053L15 10z"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const BulbActive: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 24 24"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <g fill="none" fillRule="evenodd">
        <path
          fill={color}
          d="M 12.488382,24.101644 H 11.397473 A 1.6363636,1.637629 0 0 1 9.7611098,22.464015 h 0.193091 A 2.1818182,2.1835055 0 0 1 8.0647458,20.523971 L 7.8094731,18.052242 C 7.7254731,17.239978 7.3742003,16.491036 6.8778367,15.842534 5.7814731,14.405788 5.2643822,12.505046 5.6854731,10.480937 6.2014731,8.000475 8.2032913,5.9938335 10.681837,5.4785263 a 6.5530909,6.5581586 0 0 1 7.948364,6.4031287 6.4930909,6.4981122 0 0 1 -1.336364,3.956512 c -0.496364,0.651778 -0.850909,1.400719 -0.934909,2.216259 l -0.254182,2.467361 a 2.1818182,2.1835055 0 0 1 -1.978909,1.950962 1.6363636,1.637629 0 0 1 -1.636364,1.627804 z m 3.487637,-12.085703 a 0.54545455,0.54587635 0 0 0 0.545454,-0.545876 c 0,-0.838466 -0.319636,-1.676932 -0.96,-2.3156074 v 0.0011 a 0.54545455,0.54587635 0 0 0 -0.771272,0.7718691 c 0,0.00218 0.0033,0.00328 0.0055,0.00437 0.392727,0.3952143 0.634909,0.9378153 0.634909,1.5382793 a 0.54545455,0.54587635 0 0 0 0.545455,0.545877 z M 14.125837,22.464015 H 9.9542008 c 0.09164,0.01201 0.1854542,0.01856 0.2803632,0.01856 h 3.700364 c 0.06436,0 0.127636,-0.0033 0.190909,-0.0087 z M 12.084746,3.3769023 c -0.6,0 -1.090909,-0.4912888 -1.090909,-1.0917528 V 1.1933968 c 0,-0.600464 0.490909,-1.09175273 1.090909,-1.09175273 0.6,0 1.090909,0.49128873 1.090909,1.09175273 v 1.0917527 c 0,0.600464 -0.490909,1.0917528 -1.090909,1.0917528 z m 6.171273,2.5579765 a 1.0941818,1.095028 0 0 1 0,-1.5437382 l 0.771272,-0.7718692 a 1.0941818,1.095028 0 0 1 1.542546,0 1.0941818,1.095028 0 0 1 0,1.5437383 l -0.771273,0.7718691 a 1.0941818,1.095028 0 0 1 -1.542545,0 z m 5.828727,6.1760452 c 0,0.600464 -0.490909,1.091753 -1.090909,1.091753 h -1.090909 c -0.6,0 -1.090909,-0.491289 -1.090909,-1.091753 0,-0.600464 0.490909,-1.091753 1.090909,-1.091753 h 1.090909 c 0.6,0 1.090909,0.491289 1.090909,1.091753 z m -20.7272729,0 c 0,0.600464 -0.4909091,1.091753 -1.0909091,1.091753 H 1.1756549 c -0.60000004,0 -1.09090914,-0.491289 -1.09090914,-1.091753 0,-0.600464 0.4909091,-1.091753 1.09090914,-1.091753 H 2.266564 c 0.6,0 1.0909091,0.491289 1.0909091,1.091753 z M 5.890564,5.8791995 a 1.0941818,1.095028 0 0 1 -1.5425455,0 L 3.5767458,5.1073303 a 1.0941818,1.095028 0 0 1 0,-1.5437383 1.0941818,1.095028 0 0 1 1.5425455,0 L 5.890564,4.3354611 a 1.0941818,1.095028 0 0 1 0,1.5437384 z"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const Play: React.FC<IconProps> = ({ size = 1, unit = 'em', hover = false, color = DEFAULT_COLOR, ...rest }) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 24 24"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <g fill="none" fillRule="evenodd">
        <path
          fill={color}
          d="M16.144 11.465c.473.295.47.776 0 1.07l-6.288 3.93c-.473.295-.856.08-.856-.46v-8.01c0-.55.386-.754.856-.46l6.288 3.93zM12 21c4.97 0 9-4.03 9-9s-4.03-9-9-9-9 4.03-9 9 4.03 9 9 9zm0 2C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11z"
          transform="translate(-304 -213) translate(304 213)"
        />
      </g>
    </svg>
  </SVGWrapper>
);

export const TranscriptChat: React.FC<IconProps> = ({
  size = 1,
  unit = 'em',
  hover = false,
  color = DEFAULT_COLOR,
  feColorMatrix = '1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 1 0',
  ...rest
}) => (
  <SVGWrapper hover={hover} {...rest}>
    <svg
      width={`${size}${unit}`}
      height={`${size}${unit}`}
      viewBox="0 0 24 24"
      {...extractUnnecessaryProps(rest)}
      fill={color}
    >
      <defs>
        <filter id="e1drmcg4ya">
          <feColorMatrix in="SourceGraphic" values={feColorMatrix} />
        </filter>
      </defs>
      <g fill="none" fillRule="evenodd">
        <g filter="url(#e1drmcg4ya)" transform="translate(-372 -214)">
          <g>
            <path fillRule="nonzero" d="M0 0H24V24H0z" transform="translate(372 214)" />
            <path
              fill={color}
              d="M7 5V2c0-.6.4-1 1-1s1 .4 1 1v3c0 .6-.4 1-1 1s-1-.4-1-1zM5 3c.6 0 1 .4 1 1s-.4 1-1 1H4v14h10.572c-.043.097-.08.197-.11.3l-.487 1.7H4c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h1zm15 0c1.1 0 2 .9 2 2v6.344l-2 2.001V5h-1c-.6 0-1-.4-1-1s.4-1 1-1h1zm-7 2h-2c-.6 0-1-.4-1-1s.4-1 1-1h2c.6 0 1 .4 1 1s-.4 1-1 1zm3 10c.552 0 1 .448 1 1s-.448 1-1 1H8c-.552 0-1-.448-1-1s.448-1 1-1h8zm0-3.5c.552 0 1 .448 1 1s-.448 1-1 1H8c-.552 0-1-.448-1-1s.448-1 1-1h8zM16 8c.552 0 1 .448 1 1s-.448 1-1 1H8c-.552 0-1-.448-1-1s.448-1 1-1h8zm0-7c.6 0 1 .4 1 1v3c0 .6-.4 1-1 1s-1-.4-1-1V2c0-.6.4-1 1-1zM23.153 11.404c.54-.539 1.414-.539 1.953 0l.49.489c.539.539.539 1.413 0 1.953l-.49.488-2.441-2.441.488-.489zm-7.623 8.011c.06-.21.173-.403.329-.558l5.972-5.973 2.29 2.288-5.976 5.97c-.155.155-.348.268-.559.329l-1.76.503c-.488.14-.94-.311-.8-.8l.504-1.759z"
              transform="translate(372 214)"
            />
          </g>
        </g>
      </g>
    </svg>
  </SVGWrapper>
);
